// Front-end for the Feedback page (Pages/Help/Feedback.cshtml). Five jobs:
//   1. Header search icon → opens the full-screen Help search overlay (identical
//      to the article Template page; the icon keeps href="/help" as a no-JS
//      fallback — initHelpSearchOverlay binds the trigger + preventDefault).
//   2. Form submit — the browser validates the required fields (native), then this
//      handler acks with a toast and resets. There's no server handler yet, so it
//      never actually navigates.
//   3. Sensitive-info hint popover — dismiss it on any click outside the trigger or
//      its panel (MDB popovers ship no built-in outside-click close).
//   4. Topic dropdown → tag its runtime-built options list with the .golos utility
//      (no static markup to class, and styling fonts in the page SCSS is disallowed).
//   5. Prefill the email field from the stored app email (localStorage `email`, set
//      globally by _Layout.cshtml.ts from ?email=).
// (The form controls — MDB Input / Select / form-check — and the popover are all
// auto-initialised by the CDN bundle; this file only adds the jobs above.)
import { initHelpSearchOverlay } from '../../Assets/helpSearch';

// ── Header search icon → overlay ─────────────────────────────────────────────
// initHelpSearchOverlay binds the shared [data-help-search] header icon itself.
initHelpSearchOverlay();

// ── Form submit ──────────────────────────────────────────────────────────────
const form = document.querySelector<HTMLFormElement>('.feedback-form');

form?.addEventListener('submit', (event) => {
    event.preventDefault(); // no server handler yet — ack locally
    window.toast('Thanks — your feedback has been received.');
    form.reset();
});

// ── Sensitive-info hint popover → dismiss on outside click ───────────────────
// The hint uses MDB's Popover with trigger="click", and MDB ships no outside-click /
// autoClose option for popovers (popover.js only sets trigger: 'click'). So close it
// ourselves: any click outside both the trigger button and the open panel re-toggles
// the trigger to shut it. MDB renders the panel to <body>, hence the document-level
// listener and the .popover.show open-state probe.
const hintBtn = document.querySelector<HTMLButtonElement>('.feedback-hint-btn');

if (hintBtn) {
    document.addEventListener('click', (event) => {
        const panel = document.querySelector('.popover.show');
        if (!panel) return; // nothing open

        const target = event.target as Node;
        // Leave the trigger (it toggles itself) and clicks inside the panel alone.
        if (hintBtn.contains(target) || panel.contains(target)) return;

        hintBtn.click(); // re-toggle MDB's click-trigger popover closed
    });
}

// ── Topic dropdown → Golos Text option labels ───────────────────────────────────
// The option labels should render in Golos Text via the site's .golos utility, but MDB
// builds the dropdown at runtime and portals it to <body>, so there's no static markup
// to class. Watch <body> for the dropdown container (added on first open) and tag its
// options list. MDB reuses the same element across opens, so one tag sticks — then we
// disconnect. The observer fires before paint, so the labels never flash in the wrong
// font (and the panel animates up from opacity 0 anyway).
const optionsListSelector = '#select-dropdown-container-feedback-topic .select-options-list';
const dropdownObserver = new MutationObserver(() => {
    const list = document.querySelector(optionsListSelector);
    if (list) {
        list.classList.add('golos');
        dropdownObserver.disconnect();
    }
});

dropdownObserver.observe(document.body, { childList: true });

// ── Prefill email from the stored app email ──────────────────────────────────
// _Layout.cshtml.ts stashes ?email=<user> in localStorage globally; if present, fill the
// email field. Dispatching 'input' lets MDB float its label (it tracks the input event),
// so the prefilled value doesn't sit under the resting label.
const storedEmail = localStorage.getItem('email');
const emailInput = document.querySelector<HTMLInputElement>('#feedback-email');

if (storedEmail && emailInput) {
    emailInput.value = storedEmail;
    emailInput.dispatchEvent(new Event('input', { bubbles: true }));
}
